// Fullscreen helpers. iOS Safari + on-screen keyboards drop us out of
// fullscreen as soon as the user focuses a text input (e.g. typing a
// battle room code). We re-enter on the next confirmed user gesture
// (button press) — that's the only reliable trigger because every
// browser requires a user-activation context for requestFullscreen().

let wasFullscreenBeforeBlur = false;

/** Capture whether we WERE fullscreen at the moment an input got focus.
 *  Hooked once at app boot from App.tsx. */
export function installFullscreenAutoRestore(): void {
  if (typeof window === "undefined") return;
  if ((window as any).__wrFsInstalled) return;
  (window as any).__wrFsInstalled = true;
  document.addEventListener(
    "focusin",
    (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const tag = t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable) {
        wasFullscreenBeforeBlur = !!document.fullscreenElement;
      }
    },
    true,
  );
}

/** Best-effort enter fullscreen. Call from a user-gesture handler (button
 *  click / tap). Silently ignores environments that don't support it
 *  (iOS Safari) or already-fullscreen state. */
export function tryEnterFullscreen(): void {
  if (typeof document === "undefined") return;
  if (document.fullscreenElement) return;
  const el = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  const req = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el);
  if (!req) return;
  void req().catch(() => { /* user-gesture / unsupported — ignore */ });
}

/** Re-enter fullscreen IF we were previously in fullscreen before an input
 *  blurred us out. Safe to call from any button click — only fires when
 *  the auto-restore was armed by a recent input focus event. */
export function maybeRestoreFullscreen(): void {
  if (!wasFullscreenBeforeBlur) return;
  if (document.fullscreenElement) {
    wasFullscreenBeforeBlur = false;
    return;
  }
  wasFullscreenBeforeBlur = false;
  tryEnterFullscreen();
}
